import type { Services } from '~/services'
import {
  EXCLUDED_SEARCH_CATEGORIES,
  SEARCH_CATEGORY_ICONS,
  SEARCH_CATEGORY_LABELS,
  SEARCH_RESULTS_PER_CATEGORY,
} from '~/shared/const/global-search'
import type {
  GlobalSearchItem,
  GlobalSearchItems,
  SearchCategoryItem,
  SearchResponse,
} from '~/shared/interfaces/search'

export interface HighlightPart {
  value: string
  highlighted: boolean
}

export const fetchSearchItems = async (services: Services, query: string): Promise<GlobalSearchItems> => {
  const [vacanciesResponse, searchResponse] = await Promise.all([
    services.requestRest
      .getList<SearchCategoryItem>('vacancies', {
        'sf:position_name_kw': query,
        'size': SEARCH_RESULTS_PER_CATEGORY,
      })
      .catch(() => null),
    services.searchService.getSearch(query).catch((): SearchResponse => ({})),
  ])

  const vacancyItems = getVacancyItems(vacanciesResponse?.data)
  return mergeSearchItems(vacancyItems, searchResponse)
}

export const getCategoryLabel = (key: string): string =>
  SEARCH_CATEGORY_LABELS[key as keyof typeof SEARCH_CATEGORY_LABELS] ?? 'Часто ищут'

export const getCategoryIcon = (key: string): string =>
  SEARCH_CATEGORY_ICONS[key as keyof typeof SEARCH_CATEGORY_ICONS] ?? 'fluent/search-20-regular'

export const getItemDisplayName = (category: string, item: GlobalSearchItem): string =>
  category === 'vacancies'
    ? (item.position_value ?? item.name ?? '')
    : (item.name ?? item.position_value ?? '')

export const hasSearchItems = (items: GlobalSearchItems): boolean =>
  Object.values(items).some(categoryItems => categoryItems.length > 0)

export const getFirstSearchItem = (items: GlobalSearchItems): GlobalSearchItem | undefined => {
  for (const categoryItems of Object.values(items)) {
    if (categoryItems.length > 0) {
      return categoryItems[0]
    }
  }

  return undefined
}

const getVacancyItems = (items?: SearchCategoryItem[] | null): SearchCategoryItem[] =>
  Array.isArray(items) ? items : []

const getItemLink = (key: string, item: SearchCategoryItem): string => {
  if (key === 'employers' && item.slug) {
    return `/employer/${item.slug}`
  }

  if (key === 'vacancies') {
    return `/${key}/${item.id}`
  }

  return item.slug ? `/${key}/${item.slug}` : ''
}

const mapCategoryItems = (key: string, data: SearchCategoryItem[]): GlobalSearchItem[] =>
  data
    .slice(0, SEARCH_RESULTS_PER_CATEGORY)
    .map(item => ({
      ...item,
      link: getItemLink(key, item),
    }))
    .filter(item => Boolean(item.link))

const mergeSearchItems = (vacancyItems: SearchCategoryItem[], searchItems: SearchResponse): GlobalSearchItems => {
  const items: GlobalSearchItems = {}

  if (vacancyItems.length > 0) {
    items.vacancies = mapCategoryItems('vacancies', vacancyItems)
  }

  for (const [key, categoryItems] of Object.entries(searchItems)) {
    if (EXCLUDED_SEARCH_CATEGORIES.includes(key)) {
      continue
    }

    if (!Array.isArray(categoryItems) || categoryItems.length === 0) {
      continue
    }

    const mappedItems = mapCategoryItems(key, categoryItems)

    if (mappedItems.length > 0) {
      items[key] = mappedItems
    }
  }

  return items
}

export const splitMatchParts = (text: string, query: string): HighlightPart[] => {
  const trimmed = query.trim()

  if (!trimmed) {
    return [{ value: text, highlighted: false }]
  }

  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = text.split(regex)

  if (parts.length === 1) {
    return [{ value: text, highlighted: false }]
  }

  return parts
    .filter(Boolean)
    .map(part => ({
      value: part,
      highlighted: part.toLowerCase() === trimmed.toLowerCase(),
    }))
}
